<template>
  <div class="renderPage cfg-container" :class="{ 'theme--dark': $vuetify.theme.dark }">

    <!-- TOOLBAR CABECERA -->
    <v-toolbar flat height="56" :color="$vuetify.theme.dark ? '#1e293b' : '#f1f5f9'" class="cfg-toolbar px-2">
      <v-icon class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-cog-outline</v-icon>
      <v-toolbar-title class="font-weight-black text-uppercase cfg-toolbar-title" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-4'">
        Configuración de Compañía
      </v-toolbar-title>
      <v-spacer />
      <v-btn v-if="selectedCia" depressed color="primary" class="cfg-btn font-weight-bold" :loading="isSaving" @click="guardarConfiguracion">
        <v-icon left small>mdi-content-save</v-icon> Guardar Cambios
      </v-btn>
    </v-toolbar>

    <!-- SELECTOR DE CIA -->
    <div class="px-4 pt-4 pb-2">
      <div class="cfg-section-label mb-3">
        <v-icon small class="mr-1" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-office-building-outline</v-icon>
        <span class="font-weight-black text-caption text-uppercase" style="letter-spacing:1px" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Seleccionar Compañía</span>
      </div>
      <v-card flat class="cfg-section-card pa-4 mb-4">
        <v-autocomplete
          v-model="selectedCia"
          :items="cias"
          item-text="nombre"
          item-value="cedula"
          label="Compañía"
          outlined dense hide-details
          prepend-inner-icon="mdi-office-building"
          style="max-width:500px"
          @change="cargarDatosConfiguracion"
        >
          <template v-slot:selection="data">
            <span class="font-weight-bold">{{ data.item.cedula }}</span>&nbsp;— {{ data.item.nombre }}
          </template>
          <template v-slot:item="data">
            <v-list-item-content>
              <v-list-item-title>{{ data.item.cedula }} — {{ data.item.nombre }}</v-list-item-title>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-card>
    </div>

    <!-- TABS DE CONFIGURACIÓN -->
    <div v-if="selectedCia" class="px-4">
      <v-card flat class="cfg-tabs-card">
        <v-tabs
          v-model="tab"
          height="48"
          color="primary"
          class="cfg-tabs"
          :background-color="$vuetify.theme.dark ? '#1e293b' : '#f8fafc'"
          :dark="$vuetify.theme.dark"
        >
          <v-tab class="font-weight-bold text-caption text-uppercase" style="letter-spacing:1px">
            <v-icon left small>mdi-toggle-switch</v-icon> Opciones del Sistema
          </v-tab>
          <v-tab class="font-weight-bold text-caption text-uppercase" style="letter-spacing:1px">
            <v-icon left small>mdi-tune</v-icon> Parámetros
          </v-tab>
        </v-tabs>
        <v-divider />

        <v-tabs-items v-model="tab" class="cfg-tabs-body">

          <!-- TAB 1: OPCIONES -->
          <v-tab-item class="pa-5">
            <div v-if="loadingData" class="text-center pa-10">
              <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
              <div class="mt-4 caption" :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">Cargando opciones...</div>
            </div>
            <v-row v-else dense>
              <v-col v-for="opcion in masterOpciones" :key="opcion.id" cols="12" md="6" lg="4">
                <v-card flat class="cfg-opcion-card pa-3 d-flex align-center justify-space-between">
                  <div class="d-flex align-center">
                    <v-icon small :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-2'" class="mr-3">mdi-check-circle-outline</v-icon>
                    <div>
                      <div class="font-weight-bold text-body-2">{{ opcion.id }}</div>
                      <div class="caption" :class="$vuetify.theme.dark ? 'blue-grey--text text--lighten-1' : 'grey--text'">{{ opcion.descripcion }}</div>
                    </div>
                  </div>
                  <v-switch v-model="opcion.activo" color="primary" hide-details inset class="mt-0 pt-0 flex-grow-0"></v-switch>
                </v-card>
              </v-col>
              <v-col cols="12" v-if="masterOpciones.length === 0">
                <div class="text-center pa-8" :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">No se encontraron opciones registradas.</div>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- TAB 2: PARÁMETROS -->
          <v-tab-item class="pa-5">
            <div v-if="loadingData" class="text-center pa-10">
              <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
              <div class="mt-4 caption" :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">Cargando parámetros...</div>
            </div>
            <div v-else>
              <div class="d-flex justify-end mb-4">
                <v-btn depressed color="primary" class="cfg-btn font-weight-bold" small @click="abrirDialogoParametro()">
                  <v-icon left small>mdi-plus</v-icon> Agregar Parámetro
                </v-btn>
              </div>
              <v-row dense>
                <v-col v-for="param in parametros" :key="param.nombre" cols="12" md="6" lg="4">
                  <v-card flat class="cfg-opcion-card pa-4">
                    <div class="d-flex align-center mb-3">
                      <v-icon small :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-2'" class="mr-2">{{ iconForParam(param.nombre) }}</v-icon>
                      <span class="font-weight-bold text-body-2">{{ param.nombre }}</span>
                    </div>
                    <div class="caption mb-3" :class="$vuetify.theme.dark ? 'blue-grey--text text--lighten-1' : 'grey--text'">{{ descripcionParam(param.nombre) }}</div>
                    <v-switch
                      v-if="param && param.nombre === 'Simplificado'"
                      v-model="param.valor"
                      true-value="true"
                      false-value="false"
                      :label="param.valor === 'true' ? 'Activo' : 'Inactivo'"
                      color="primary"
                      hide-details inset
                      class="mt-0 pt-0"
                    ></v-switch>
                    <v-text-field
                      v-else-if="param"
                      v-model="param.valor"
                      :label="labelForParam(param.nombre)"
                      outlined dense hide-details
                    ></v-text-field>
                  </v-card>
                </v-col>
                <v-col cols="12" v-if="parametros.length === 0">
                  <div class="text-center pa-8">
                    <v-icon size="40" :color="$vuetify.theme.dark ? 'blue-grey darken-1' : 'grey lighten-1'" class="mb-3">mdi-alert-circle-outline</v-icon>
                    <div :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">No hay parámetros registrados para esta compañía.</div>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-tab-item>

        </v-tabs-items>
      </v-card>
    </div>

    <!-- DIÁLOGO NUEVO PARÁMETRO -->
    <v-dialog v-model="dialogParametro" max-width="460" persistent>
      <v-card flat class="cfg-dialog-card">
        <div class="cfg-drawer-header d-flex align-center px-4 py-3"
             :class="$vuetify.theme.dark ? 'cfg-drawer-header--dark' : ''">
          <v-icon color="white" class="mr-3">mdi-cog-plus-outline</v-icon>
          <div>
            <div class="white--text font-weight-black text-uppercase" style="letter-spacing:1px;font-size:0.95rem;">Agregar Parámetro</div>
            <div class="caption white--text" style="opacity:0.7">Selecciona y configura el parámetro</div>
          </div>
          <v-spacer />
          <v-btn icon dark small @click="dialogParametro = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <div class="pa-5">
          <v-select
            v-model="nuevoParametro.nombre"
            :items="parametrosDisponibles"
            item-text="label"
            item-value="nombre"
            label="Parámetro"
            outlined dense
            prepend-inner-icon="mdi-format-list-bulleted"
            @change="prepararFormularioNuevoParametro"
          >
            <template v-slot:item="{ item }">
              <v-list-item-icon><v-icon small>{{ item.icon }}</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ item.nombre }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.descripcion }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-select>

          <template v-if="nuevoParametro.nombre">
            <v-switch
              v-if="nuevoParametro.nombre === 'Simplificado'"
              v-model="nuevoParametro.valor"
              true-value="true"
              false-value="false"
              :label="nuevoParametro.valor === 'true' ? 'Activo' : 'Inactivo'"
              color="primary"
              hide-details inset
              class="mt-1"
            ></v-switch>
            <v-autocomplete
              v-else-if="nuevoParametro && nuevoParametro.nombre === 'ClientePOSDefecto'"
              v-model="nuevoParametro.valor"
              :items="clientesCargados"
              item-text="nombre"
              item-value="codigo"
              :label="labelForParam(nuevoParametro.nombre)"
              outlined dense
              prepend-inner-icon="mdi-account-star-outline"
            >
              <template v-slot:selection="data">{{ data.item ? data.item.codigo : '' }} — {{ data.item ? data.item.nombre : '' }}</template>
              <template v-slot:item="data">
                <v-list-item-content>
                  <v-list-item-title>{{ data.item ? data.item.codigo : '' }} — {{ data.item ? data.item.nombre : '' }}</v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>
            <v-text-field
              v-else
              v-model="nuevoParametro.valor"
              :label="labelForParam(nuevoParametro.nombre)"
              outlined dense
              :prepend-inner-icon="nuevoParametro.nombre === 'CorreoContador' ? 'mdi-email-outline' : 'mdi-account-outline'"
            ></v-text-field>
          </template>

          <div class="d-flex justify-end mt-2">
            <v-btn text class="mr-2 cfg-btn font-weight-bold" @click="dialogParametro = false">Cancelar</v-btn>
            <v-btn color="primary" depressed class="cfg-btn font-weight-bold px-6" :disabled="!nuevoParametro.nombre" @click="agregarParametro">
              <v-icon left small>mdi-plus</v-icon> Agregar
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <Snackbar v-bind:text="snackText" v-bind:snackbar.sync="snackValue" />
  </div>
</template>

<script>
import axios from "axios";
import Snackbar from "./shared/Snackbar.vue";

export default {
  name: "ConfiguracionCia",
  components: { Snackbar },
  data() {
    return {
      tab: 0,
      selectedCia: null,
      cias: [],
      masterOpciones: [],
      parametros: [],
      loadingData: false,
      isSaving: false,
      dialogParametro: false,
      clientesCargados: [],
      nuevoParametro: { nombre: '', valor: '' },
      snackText: "",
      snackValue: false,
      catalogoParametros: [
        {
          nombre: 'Simplificado',
          label: 'Simplificado',
          descripcion: 'Régimen de tributación simplificada (sin desglose de IVA)',
          icon: 'mdi-receipt-text-outline',
        },
        {
          nombre: 'ClientePOSDefecto',
          label: 'ClientePOSDefecto',
          descripcion: 'Código del cliente que carga por defecto al abrir el POS',
          icon: 'mdi-account-star-outline',
        },
        {
          nombre: 'CorreoContador',
          label: 'CorreoContador',
          descripcion: 'Correo del contador para envío de reportes',
          icon: 'mdi-email-outline',
        },
      ],
    };
  },
  computed: {
    parametrosDisponibles() {
      return this.catalogoParametros.filter(
        p => !this.parametros.find(ep => ep.nombre === p.nombre)
      );
    },
  },
  created() {
    this.getCias();
  },
  methods: {
    getCias() {
      axios.get("api/Cias/getCias")
        .then(r => { this.cias = r.data; })
        .catch(e => { console.error(e); this.showSnackbar("Error al cargar las compañías"); });
    },
    async cargarDatosConfiguracion() {
      if (!this.selectedCia) return;
      this.loadingData = true;
      try {
        const masterRes = await axios.get("api/General/getOpcionesSistema");
        let opciones = masterRes.data.map(opt => ({ ...opt, activo: false, valor: "" }));
        const ciaOpcRes = await axios.get(`api/General/getOpcionesSistemaCia?cia=${this.selectedCia}`);
        ciaOpcRes.data.forEach(oc => {
          const mOpt = opciones.find(o => o.id === oc.opcionId);
          if (mOpt) { mOpt.activo = true; mOpt.valor = oc.valor || ""; }
        });
        this.masterOpciones = opciones;

        const paramRes = await axios.get(`api/General/getParametros?cia=${this.selectedCia}`);
        let pData = Array.isArray(paramRes.data) ? paramRes.data : [];
        this.parametros = pData.map(p => {
          if (!p) return p;
          if (p.nombre === 'Simplificado' && p.valor) p.valor = String(p.valor).toLowerCase();
          return p;
        }).filter(p => p !== null && p !== undefined);
      } catch (e) {
        console.error(e);
        this.showSnackbar("Error de conexión con el servidor");
      } finally {
        this.loadingData = false;
      }
    },
    async guardarConfiguracion() {
      if (!this.selectedCia) return;
      this.isSaving = true;
      try {
        const opcionesToSave = this.masterOpciones
          .filter(opt => opt.activo)
          .map(opt => ({ Cia: this.selectedCia, OpcionId: opt.id, Valor: opt.valor }));
        await Promise.all([
          axios.post(`api/General/saveOpcionesSistemaCia?cia=${this.selectedCia}`, opcionesToSave),
          axios.post(`api/General/saveParametros?cia=${this.selectedCia}`, this.parametros)
        ]);
        this.showSnackbar("Configuración guardada exitosamente");
      } catch (e) {
        console.error(e);
        this.showSnackbar("Error al registrar los cambios");
      } finally {
        this.isSaving = false;
      }
    },
    showSnackbar(text) {
      this.snackText = text;
      this.snackValue = true;
    },
    abrirDialogoParametro() {
      this.nuevoParametro = { nombre: '', valor: '' };
      this.dialogParametro = true;
    },
    async prepararFormularioNuevoParametro(nombreSeleccionado) {
      this.nuevoParametro.valor = '';
      if (nombreSeleccionado === 'ClientePOSDefecto' && this.clientesCargados.length === 0) {
        try {
          const res = await axios.get(`api/Clientes/getClientesByCompany?cia=${this.selectedCia}`);
          this.clientesCargados = res.data;
        } catch (e) {
          console.error(e);
          this.showSnackbar("Error al cargar la lista de clientes");
        }
      }
    },
    agregarParametro() {
      if (!this.nuevoParametro.nombre) { this.showSnackbar("Selecciona un parámetro"); return; }
      this.parametros.push({
        nombre: this.nuevoParametro.nombre,
        valor: this.nuevoParametro.valor || 'false',
        cia: this.selectedCia,
      });
      this.dialogParametro = false;
    },
    iconForParam(nombre) {
      const p = this.catalogoParametros.find(c => c.nombre === nombre);
      return p ? p.icon : 'mdi-cog-outline';
    },
    descripcionParam(nombre) {
      const p = this.catalogoParametros.find(c => c.nombre === nombre);
      return p ? p.descripcion : '';
    },
    labelForParam(nombre) {
      if (nombre === 'ClientePOSDefecto') return 'Código del cliente (ej. CL-00004)';
      if (nombre === 'CorreoContador') return 'Correo electrónico del contador';
      return 'Valor';
    },
  },
};
</script>

<style scoped>
/* ===== CONTAINER ===== */
.cfg-container { background-color: #f1f5f9; min-height: 100vh; }
.theme--dark.cfg-container { background-color: #0f172a; }

/* ===== TOOLBAR ===== */
.cfg-toolbar { border-bottom: 1px solid rgba(0,0,0,0.1) !important; }
.theme--dark .cfg-toolbar { border-bottom: 1px solid rgba(255,255,255,0.07) !important; }
.cfg-toolbar-title { font-size: 0.95rem !important; letter-spacing: 1.5px; }

/* ===== BUTTONS ===== */
.cfg-btn { border-radius: 4px !important; text-transform: uppercase; letter-spacing: 0.8px; font-size: 0.75rem !important; }

/* ===== SECTION ===== */
.cfg-section-label { display: flex; align-items: center; }
.cfg-section-card { border: 1px solid rgba(0,0,0,0.07) !important; border-radius: 4px !important; background-color: #ffffff !important; }
.theme--dark .cfg-section-card { border-color: rgba(255,255,255,0.07) !important; background-color: #1e293b !important; }

/* ===== TABS CARD ===== */
.cfg-tabs-card { border: 1px solid rgba(0,0,0,0.07) !important; border-radius: 4px !important; overflow: hidden; }
.theme--dark .cfg-tabs-card { border-color: rgba(255,255,255,0.07) !important; }

/* ===== TABS ===== */
.cfg-tabs >>> .v-tabs-bar { border-bottom: 1px solid rgba(0,0,0,0.08) !important; }
.cfg-tabs >>> .v-tab { font-size: 0.72rem !important; letter-spacing: 1px; }
.cfg-tabs-body { background-color: #f8fafc !important; }
.theme--dark .cfg-tabs-body { background-color: #0f172a !important; }

/* ===== OPCION CARD ===== */
.cfg-opcion-card { border: 1px solid rgba(0,0,0,0.07) !important; border-radius: 4px !important; background-color: #ffffff !important; }
.theme--dark .cfg-opcion-card { border-color: rgba(255,255,255,0.07) !important; background-color: #1e293b !important; }

/* ===== DIALOG ===== */
.cfg-dialog-card { border-radius: 4px !important; overflow: hidden; }

/* ===== DRAWER HEADER (reutilizado en dialog) ===== */
.cfg-drawer-header { background-color: #37474f; flex-shrink: 0; }
.cfg-drawer-header--dark { background-color: #263238 !important; }
</style>
